import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import type { WizardData } from "@/pages/CreateCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CalendarIcon, HelpCircle, MapPin, UserPlus, Users } from "lucide-react";
import RsvpQuestionBuilder, { type RsvpQuestion } from "@/components/card/RsvpQuestionBuilder";
import { cn } from "@/lib/utils";

const HelpTip = ({ text }: { text: string }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle size={14} className="text-muted-foreground/50 cursor-help" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-xs">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

interface Props {
  data: WizardData;
  updateData: (u: Partial<WizardData>) => void;
}

/**
 * StepRsvp — optional wizard step that mirrors the RSVP section of
 * the Card Manage → Settings modal. The organizer flips the toggle,
 * fills venue / date / plus-one / capacity / questions, and we flow
 * those through to the collection's rsvp_* columns on save.
 *
 * The fields stay hidden behind the toggle when RSVP is off, so
 * cards that don't need an in-person event don't have to scroll
 * past an empty form.
 */
const StepRsvp = ({ data, updateData }: Props) => {
  const { t } = useTranslation();

  const rsvpEventDate = data.rsvpEventDate ? new Date(data.rsvpEventDate) : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground font-serif">
          {t("wizard.rsvp.title", { defaultValue: "RSVP" })}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t("wizard.rsvp.subtitle", {
            defaultValue:
              "If this card is tied to a gathering, turn on RSVP so guests can confirm. You can also add this later from the card manage page.",
          })}
        </p>
      </div>

      {/* Master toggle */}
      <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
        <div>
          <p className="text-sm font-medium text-foreground">
            {t("wizard.rsvp.enable", { defaultValue: "Enable RSVP" })}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t("wizard.rsvp.enableDesc", {
              defaultValue: "Collect guest responses, event details, and optional questions.",
            })}
          </p>
        </div>
        <Switch
          checked={!!data.rsvpEnabled}
          onCheckedChange={(v) => updateData({ rsvpEnabled: v })}
        />
      </div>

      {data.rsvpEnabled && (
        <div className="space-y-5">
          {/* Venue */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">
              <MapPin size={12} className="inline me-1" />
              {t("wizard.rsvp.venueName", { defaultValue: "Venue name" })}
            </Label>
            <Input
              value={data.rsvpLocationName}
              onChange={(e) => updateData({ rsvpLocationName: e.target.value })}
              placeholder={t("wizard.rsvp.venueNamePlaceholder", {
                defaultValue: "e.g. The Rooftop Restaurant",
              })}
              className="h-12 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">
              {t("wizard.rsvp.address", { defaultValue: "Address" })}
            </Label>
            <Input
              value={data.rsvpLocationAddress}
              onChange={(e) => updateData({ rsvpLocationAddress: e.target.value })}
              placeholder={t("wizard.rsvp.addressPlaceholder", {
                defaultValue: "Street, city — optional",
              })}
              className="h-12 text-sm"
            />
          </div>

          {/* Event date + time */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">
              <CalendarIcon size={12} className="inline me-1" />
              {t("wizard.rsvp.eventDateTime", { defaultValue: "Event date & time" })}
            </Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 justify-start text-start text-sm h-12",
                      !rsvpEventDate && "text-muted-foreground",
                    )}
                  >
                    {rsvpEventDate
                      ? format(rsvpEventDate, "dd MMM yyyy")
                      : t("wizard.rsvp.pickDate", { defaultValue: "Pick a date" })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={rsvpEventDate}
                    onSelect={(date) =>
                      updateData({ rsvpEventDate: date ? date.toISOString() : "" })
                    }
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <Input
                type="time"
                value={data.rsvpEventTime || "18:00"}
                onChange={(e) => updateData({ rsvpEventTime: e.target.value })}
                className="w-32 h-12 text-sm"
              />
            </div>
          </div>

          {/* Plus-one */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <UserPlus size={14} className="text-muted-foreground" />
              <span className="text-sm text-foreground">
                {t("wizard.rsvp.allowPlusOne", { defaultValue: "Allow plus-one" })}
              </span>
            </div>
            <Switch
              checked={!!data.rsvpPlusOne}
              onCheckedChange={(v) => updateData({ rsvpPlusOne: v })}
            />
          </div>

          {/* Capacity */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Label className="text-xs text-muted-foreground block">
                <Users size={12} className="inline me-1" />
                {t("wizard.rsvp.maxCapacity", { defaultValue: "Maximum capacity" })}
              </Label>
              <HelpTip
                text={t("wizard.rsvp.maxCapacityDesc", {
                  defaultValue:
                    "Leave empty for unlimited. A waitlist opens when capacity is reached.",
                })}
              />
            </div>
            <Input
              type="number"
              min={1}
              value={data.rsvpMaxCapacity}
              onChange={(e) => updateData({ rsvpMaxCapacity: e.target.value })}
              placeholder={t("wizard.rsvp.maxCapacityPlaceholder", {
                defaultValue: "Unlimited",
              })}
              className="h-12 text-sm"
            />
          </div>

          {/* Invite-only */}
          <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {t("wizard.rsvp.inviteOnly", { defaultValue: "Invite-only event" })}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t("wizard.rsvp.inviteOnlyDesc", {
                  defaultValue:
                    "Only whitelisted emails can view event details. Manage guests later via Invite People.",
                })}
              </p>
            </div>
            <Switch
              checked={!!data.rsvpInviteOnly}
              onCheckedChange={(v) => updateData({ rsvpInviteOnly: v })}
            />
          </div>

          {/* Custom RSVP questions — reuse the existing builder so
              wizard and manage produce the same shape. */}
          <div className="pt-1">
            <RsvpQuestionBuilder
              questions={data.rsvpQuestions}
              onChange={(questions: RsvpQuestion[]) => updateData({ rsvpQuestions: questions })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StepRsvp;
